'use client';

import {
  Alert,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { JobTemplate, SparkEngine } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { Panels } from '@/components/panels/panels';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace as useGlobalWorkspace } from '@/contexts/useWorkspace';
import { PanelSize } from '@/contexts/workspaceContext';
import { dayjs } from '@/utils/dayjs';

import { DeleteJobTemplateDialog } from './deleteJobTemplateDialog';

import { eonSparkVersionLabel } from '../eonSparkVersionUtils';

interface JobTemplatePanelProps {
  id: string;
  workspaceId: string;
  onClose: () => void;
  onRefresh: () => void;
}

export const JobTemplatePanel = ({
  id,
  workspaceId,
  onClose,
  onRefresh,
}: JobTemplatePanelProps) => {
  const dal = useDAL();
  const { isAuthorized } = useRoles();
  const { rightPanel } = useGlobalWorkspace();
  const [view, setView] = useState<'details' | 'submit'>('details');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: templatesData, isLoading: isTemplatesLoading } =
    dal.lakehouse.useListJobTemplates(workspaceId);

  const template = templatesData?.jobTemplates?.find(
    (t: JobTemplate) => t.id === id
  );

  if (isTemplatesLoading || !template) {
    return (
      <PanelWrapper
        header={{
          title: 'Job Template Details',
          onClose,
        }}
      >
        <Stack alignItems='center' justifyContent='center' flex={1}>
          <CircularProgress />
        </Stack>
      </PanelWrapper>
    );
  }

  const canCreateJob = isAuthorized('create:spark_job');
  const canUpdateTemplate = isAuthorized('update:job_template');
  const canDeleteTemplate = isAuthorized('delete:job_template');

  const handleEdit = () => {
    rightPanel.setIsOpen(true, PanelSize.Full);
    rightPanel.setComponent({
      panel: Panels.UpdateJobTemplateWizard,
      props: {
        workspaceId,
        template,
        onAbort: () => {
          rightPanel.setIsOpen(true, PanelSize.Large);
          rightPanel.setComponent({
            panel: Panels.JobTemplateInstance,
            props: { id, workspaceId, onClose, onRefresh },
          });
        },
        onUpdated: () => onRefresh(),
      },
    });
  };

  if (view === 'submit') {
    return (
      <SubmitJobFromTemplateView
        template={template}
        workspaceId={workspaceId}
        onClose={onClose}
        onBack={() => setView('details')}
        onSubmitted={onRefresh}
      />
    );
  }

  return (
    <>
      <PanelWrapper
        header={{
          title: 'Job Template Details',
          onClose,
          actions: (
            <>
              {canCreateJob && (
                <ButtonWithIcon
                  variant='outlined'
                  size='small'
                  onClick={() => setView('submit')}
                  text='Submit Job'
                  icon='material-symbols-play-arrow-rounded'
                />
              )}
              <OptionMenu
                options={[
                  {
                    id: 'edit',
                    text: 'Edit',
                    icon: 'material-symbols-edit-outline-rounded',
                    menuItemProps: {
                      disabled: !canUpdateTemplate,
                      onClick: handleEdit,
                    },
                  },
                  {
                    id: 'divider',
                    divider: true,
                  },
                  {
                    id: 'delete',
                    text: 'Delete',
                    icon: 'material-symbols-delete-outline-rounded',
                    menuItemProps: {
                      disabled: !canDeleteTemplate,
                      onClick: () => setDeleteDialogOpen(true),
                    },
                  },
                ]}
              />
            </>
          ),
        }}
      >
        <Stack gap='24px' padding='24px 40px'>
          {/* Template Details */}
          <Stack gap='12px'>
            <DetailRow label='ID' value={template.id} monospace />
            <DetailRow label='Name' value={template.name} />
            <DetailRow
              label='Description'
              value={template.description ?? '-'}
            />
            <DetailRow
              label='Created'
              value={dayjs
                .utc(template.createdAt)
                .format('MMM D, YYYY HH:mm:ss')}
            />
            <DetailRow
              label='Eon Spark Versions'
              value={
                template.eonSparkVersions &&
                template.eonSparkVersions.length > 0
                  ? template.eonSparkVersions
                      .map(eonSparkVersionLabel)
                      .join(', ')
                  : 'All'
              }
            />
            {template.jobTimeout && (
              <DetailRow
                label='Job Timeout'
                value={`${template.jobTimeout} seconds`}
              />
            )}
          </Stack>

          <Divider />

          {/* Spark Submit Parameters */}
          <Stack gap='12px'>
            <Typography variant='subtitle2' color='text.secondary'>
              Spark Submit Parameters
            </Typography>
            {template.sparkSubmitParams && (
              <>
                <DetailRow
                  label='Entry Point'
                  value={template.sparkSubmitParams.entryPoint}
                  monospace
                />
                {template.sparkSubmitParams.entryPointArguments &&
                  template.sparkSubmitParams.entryPointArguments.length > 0 && (
                    <DetailRow
                      label='Arguments'
                      value={template.sparkSubmitParams.entryPointArguments.join(
                        ' '
                      )}
                      monospace
                    />
                  )}
                {template.sparkSubmitParams.sparkSubmitParameters &&
                  Object.keys(template.sparkSubmitParams.sparkSubmitParameters)
                    .length > 0 && (
                    <DetailRow
                      label='Spark Config'
                      value={Object.entries(
                        template.sparkSubmitParams.sparkSubmitParameters
                      )
                        .map(([k, v]) => `${k}=${v}`)
                        .join(', ')}
                      monospace
                    />
                  )}
                {template.sparkSubmitParams.environmentVariables &&
                  Object.keys(template.sparkSubmitParams.environmentVariables)
                    .length > 0 && (
                    <DetailRow
                      label='Env Variables'
                      value={Object.entries(
                        template.sparkSubmitParams.environmentVariables
                      )
                        .map(([k, v]) => `${k}=${v}`)
                        .join(', ')}
                      monospace
                    />
                  )}
              </>
            )}
          </Stack>

          {/* Storage Config */}
          {template.storageConfig && (
            <>
              <Divider />
              <Stack gap='12px'>
                <Typography variant='subtitle2' color='text.secondary'>
                  Storage
                </Typography>
                {template.storageConfig.driverDiskSize && (
                  <DetailRow
                    label='Driver Disk Size'
                    value={`${template.storageConfig.driverDiskSize} GB`}
                  />
                )}
                {template.storageConfig.executorDiskSize && (
                  <DetailRow
                    label='Executor Disk Size'
                    value={`${template.storageConfig.executorDiskSize} GB`}
                  />
                )}
              </Stack>
            </>
          )}

          {/* Retry Policy */}
          {template.retryPolicy && (
            <>
              <Divider />
              <Stack gap='12px'>
                <Typography variant='subtitle2' color='text.secondary'>
                  Retry Policy
                </Typography>
                <DetailRow
                  label='Max Attempts'
                  value={String(template.retryPolicy.maxAttempts ?? 0)}
                />
                {template.retryPolicy.cooldownInterval && (
                  <DetailRow
                    label='Cooldown Interval'
                    value={`${template.retryPolicy.cooldownInterval} seconds`}
                  />
                )}
              </Stack>
            </>
          )}
        </Stack>
      </PanelWrapper>

      <DeleteJobTemplateDialog
        template={template}
        workspaceId={workspaceId}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={() => {
          onRefresh();
          onClose();
        }}
      />
    </>
  );
};

/* ---------- Submit Job inline view ---------- */

const SubmitJobFromTemplateView = ({
  template,
  workspaceId,
  onClose,
  onBack,
  onSubmitted,
}: {
  template: JobTemplate;
  workspaceId: string;
  onClose: () => void;
  onBack: () => void;
  onSubmitted: () => void;
}) => {
  const dal = useDAL();
  const [jobName, setJobName] = useState(template.name);
  const [selectedEngineId, setSelectedEngineId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: sparkEnginesData, isLoading: isLoadingEngines } =
    dal.lakehouse.useListSparkEngines(workspaceId);

  const runningEngines =
    sparkEnginesData?.sparkEngines?.filter((engine: SparkEngine) => {
      if (engine.status !== 'STARTED') return false;
      if (template.eonSparkVersions && template.eonSparkVersions.length > 0) {
        return template.eonSparkVersions.includes(engine.eonSparkVersion);
      }
      return true;
    }) ?? [];

  const canSubmit =
    jobName.trim().length >= 3 && !!selectedEngineId && !isSubmitting;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await dal.lakehouse.startSparkJob(workspaceId, {
        sparkEngineId: selectedEngineId,
        name: jobName.trim(),
        templateParams: { jobTemplateId: template.id },
      });
      onSubmitted();
      onBack();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit job');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PanelWrapper
      header={{
        title: 'Submit Job',
        onClose,
      }}
    >
      <Stack gap='24px' padding='24px 40px' flex={1}>
        <Typography variant='body2' color='text.secondary'>
          Submit a new Spark job using the &quot;{template.name}&quot; template.
        </Typography>

        <FormControl sx={{ maxWidth: '50%' }}>
          <FormLabel>
            <Typography variant='subtitle1'>Job Name</Typography>
          </FormLabel>
          <TextField
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
            disabled={isSubmitting}
            size='small'
            placeholder='Enter job name'
            error={jobName.length > 0 && jobName.trim().length < 3}
          />
          {jobName.length > 0 && jobName.trim().length < 3 && (
            <FormHelperText error>
              Name must be at least 3 characters
            </FormHelperText>
          )}
        </FormControl>

        <FormControl sx={{ maxWidth: '50%' }}>
          <FormLabel>
            <Typography variant='subtitle1'>Spark Engine</Typography>
          </FormLabel>
          {runningEngines.length === 0 && !isLoadingEngines && (
            <FormHelperText sx={{ mx: 0, mb: '8px' }}>
              {template.eonSparkVersions && template.eonSparkVersions.length > 0
                ? `No started engines with ${template.eonSparkVersions.map(eonSparkVersionLabel).join(' or ')} available.`
                : 'No started Spark engines available. Please start an engine first.'}
            </FormHelperText>
          )}
          <Select
            value={selectedEngineId}
            onChange={(e) => setSelectedEngineId(e.target.value)}
            disabled={
              isLoadingEngines ||
              isSubmitting ||
              (!isLoadingEngines && runningEngines.length === 0)
            }
            size='small'
            displayEmpty
            renderValue={(value) => {
              if (!value) {
                return runningEngines.length === 0 && !isLoadingEngines ? (
                  <Typography color='text.secondary'>&nbsp;</Typography>
                ) : (
                  <Typography color='text.secondary'>
                    Select a running Spark engine
                  </Typography>
                );
              }
              const engine = runningEngines.find(
                (e: SparkEngine) => e.id === value
              );
              return engine?.name ?? '';
            }}
          >
            <MenuItem value='' disabled>
              Select a running Spark engine
            </MenuItem>
            {isLoadingEngines ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: '8px' }} />
                Loading engines...
              </MenuItem>
            ) : (
              runningEngines.map((engine: SparkEngine) => (
                <MenuItem key={engine.id} value={engine.id}>
                  {engine.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {error && (
          <Alert severity='error'>
            <Typography variant='body2'>{error}</Typography>
          </Alert>
        )}

        <Stack
          direction='row'
          gap='12px'
          justifyContent='flex-end'
          marginTop='auto'
        >
          <Button variant='text' onClick={onBack} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </Stack>
      </Stack>
    </PanelWrapper>
  );
};

/* ---------- Detail row helper ---------- */

const DetailRow = ({
  label,
  value,
  monospace,
}: {
  label: string;
  value?: React.ReactNode;
  monospace?: boolean;
}) => {
  return (
    <Stack direction='row' alignItems='flex-start' gap='16px'>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ minWidth: '120px' }}
      >
        {label}
      </Typography>
      {typeof value === 'string' ? (
        <Typography
          variant='body2'
          sx={{
            wordBreak: 'break-word',
            flex: 1,
            ...(monospace && { fontFamily: 'monospace', fontSize: '0.85rem' }),
          }}
        >
          {value || '-'}
        </Typography>
      ) : (
        (value ?? (
          <Typography variant='body2' sx={{ flex: 1 }}>
            -
          </Typography>
        ))
      )}
    </Stack>
  );
};
